import React, {ReactElement, ReactNode} from 'react';
import {
  Card,
  CardContent,
  createStyles,
  Divider,
  makeStyles,
  Theme,
  Typography
} from '@material-ui/core';
import {useDispatch, useSelector} from 'react-redux';
import {Trans, useTranslation} from 'react-i18next';

// components, variable styles, constants
import NavRoutes from 'src/constants/route/NavRoutes';
import {
  APP,
  DEFAULT_BLACK_COLOR,
  DEFAULT_GREY_COLOR,
  DIVIDER_BLACK_COLOR,
  GET_8_PIXEL_BOX
} from 'src/style/variable';
import BackNavigation from '../backNavigation/BackNavigation';

// styles
import './PaymentsDetailsStyles.scss';
import {useHistory, useParams} from 'react-router-dom';
import {fetchGuarantorPaymentHistory} from '@shared/services/Payment.service';
import {_patientPaymentDetailsId} from '@shared/store/actions/PaymentActions';

import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import HelperUtils from '@shared/utils/HelperUtils';

import GridTable, {
  DEFAULT_GRID_TABLE__OPTIONS
} from '@components/system/GridTable/GridTable';
import {
  dateOfPaymentCell,
  headerRightAlign,
  paymentTableHeader,
  tableEvenRow,
  tableOddRow
} from 'src/modules/admin/modules/Invoices/TableStyles';
import Loader from '@components/framework/loader/Loader';
import {ChargeTypeLabels} from '@shared/constants/Mapping';

import AllPaymentPlansCard from 'src/modules/admin/modules/Invoices/Drawer/AllPaymentPlansCard/AllPaymentPlansCard';

const {innerWidth: width} = window;
const isDesktop = width >= 1024;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loginButton: {
      padding: `${GET_8_PIXEL_BOX(1)} 0`,
      fontFamily: APP.FONT_FAMILY.BOLD,
      height: '2.7rem'
    },
    card: {
      borderRadius: 0,
      color: theme.palette.primary.contrastText,
      boxShadow: 'none'
    },
    cardPadding: {
      '& .MuiCardContent-root': {
        padding: 0
      },
      padding: 0
    },
    paymentsHeaderView: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    paymentTypeHeader: {
      color: DEFAULT_BLACK_COLOR,
      fontSize: '1.3rem',
      fontFamily: APP.FONT_FAMILY.MEDIUM
    },
    hr: {
      marginTop: '0.5rem'
    },
    cellPadding: {
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem'
    },
    paymentDateView: {
      flex: 1,
      flexDirection: 'column',
      display: 'flex'
    },
    description: {
      color: DEFAULT_GREY_COLOR,
      fontSize: '0.9rem'
    },
    totalPaidView: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '1rem',
      marginBottom: '1rem'
    },
    headerPadding: {
      paddingBottom: '1rem'
    },
    lineHeight: {
      lineHeight: '1.3rem'
    },
    paymentPlanSection: {
      paddingLeft: `${GET_8_PIXEL_BOX(3)}`,
      paddingRight: `${GET_8_PIXEL_BOX(3)}`,
      paddingBottom: `${GET_8_PIXEL_BOX(3)}`
    },
    dividerView: {
      borderTop: `1px solid ${DIVIDER_BLACK_COLOR}`,
      paddingBottom: '1.3rem'
    },
    paymentPlanPadding: {
      '& .MuiCard-root': {
        padding: '1rem'
      }
    },
    clinicNameView: {
      display: 'flex',
      gap: '0.2rem',
      marginTop: '0.2rem'
    }
  })
);

const GuarantorPaymentDetails = (props: any): ReactElement => {
  const classes = useStyles();
  const history = useHistory();
  const params = useParams();

  const forceUpdate = props.forceUpdate;

  const [invoiceLines, setInvoiceLines]: any = React.useState([]);
  const [refunds, setRefunds]: any = React.useState([]);
  const [loader, setLoader] = React.useState<boolean>(true);

  const [activePaymentPlan, setActivePaymentPlan]: any = React.useState({});

  const {t} = useTranslation();

  const dispatch = useDispatch();

  const chargeTypeLabels = ChargeTypeLabels.map(item => {
    item.name = t(item.name);
    return item;
  });

  const guarantorDetails = useSelector(
    (state: any) => state.payments.baseDetails?.guarantorDetails || {}
  );
  const guarantorVerifyDetails = useSelector(
    (state: any) => state.payments.clinicDetails || {}
  );

  const findChargeTypeObj = (id: any) => {
    const [item] = chargeTypeLabels.filter(item1 => item1.id === id);
    if (item) {
      return item.name;
    }
  };

  const backNavigation = (): void => {
    dispatch(_patientPaymentDetailsId({}));

    if ('token' in params) {
      history.push(
        `/${NavRoutes.PUBLIC.PAYMENTS_DASHBOARD}/${(params as any)['token']}`
      );
    }
  };

  React.useEffect(() => {
    fetchGuarantorPaymentHistory()
      .then(res => {
        if (res.data) {
          setInvoiceLines(res.data.guarantorPayments);
          setRefunds(res.data.refunds);
          setActivePaymentPlan(res.data.paymentPlan);
        }
      })
      .catch(err => {
        if (err) {
          backNavigation();
        }
      })
      .finally(() => {
        setLoader(false);
      });
    // need to refetch the guarantor payment history on force update of paymentsDashboard
  }, [forceUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderGuarantorPayments = (
    data: any,
    header: string,
    dateKey: string,
    amountKey: string,
    descriptionKey: string
  ): ReactElement => {
    return (
      <GridTable
        columns={[
          {
            title: <Trans i18nKey={header} components={[<br key={'X'} />]} />,
            render: (rowData: any) => {
              return (
                <div className={classes.paymentDateView}>
                  <span className={classes.lineHeight}>
                    {HelperUtils.formatDisplayDate(rowData[dateKey])}
                  </span>
                  {/* PAYMENT DESCRIPTION */}
                  {rowData[descriptionKey] && (
                    <span className={classes.lineHeight}>
                      {rowData[descriptionKey]}
                    </span>
                  )}
                  {rowData.payment_status && (
                    <span
                      className={[classes.lineHeight, classes.description].join(
                        ' '
                      )}>
                      {findChargeTypeObj(rowData.payment_status)}
                    </span>
                  )}
                  {guarantorVerifyDetails?.clinic_group_details
                    ?.consolidated_group_billing_enable &&
                    rowData.clinic_name && (
                      <div className={classes.clinicNameView}>
                        <span>{t('PAYMENT_HISTORY.CLINIC')}:</span>
                        <span>
                          <b> {rowData.clinic_name}</b>
                        </span>
                      </div>
                    )}
                </div>
              );
            },
            cellStyle: dateOfPaymentCell
          },
          {
            title: t('PAYMENTS_LOGIN.DASHBOARD.DETAILS.HEADERS.AMOUNT'),
            cellStyle: {
              ...dateOfPaymentCell,
              ...headerRightAlign
            } as React.CSSProperties,
            headerStyle: {...headerRightAlign} as React.CSSProperties,
            render: (rowData: any) => (
              <div className="description-row">
                {rowData[amountKey] !== null ? (
                  <span className={classes.lineHeight}>
                    <CurrencyFormatter
                      value={rowData[amountKey] || '0'}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                      ignoreRound={true}
                      useSign={true}
                    />
                  </span>
                ) : (
                  <span />
                )}
              </div>
            )
          }
        ]}
        options={{
          ...DEFAULT_GRID_TABLE__OPTIONS,
          search: false,
          showTitle: false,
          toolbar: false,
          sorting: false,
          paging: false,
          emptyRowsWhenPaging: false,
          showEmptyDataSourceMessage: true,
          maxBodyHeight: '100%',
          tableLayout: 'auto',
          selection: false,
          draggable: false,
          headerStyle: {...paymentTableHeader} as React.CSSProperties,
          rowStyle: (rowData: any, index) => {
            if (index % 2) {
              return {...tableEvenRow} as React.CSSProperties;
            } else {
              return {...tableOddRow} as React.CSSProperties;
            }
          }
        }}
        data={data}
        style={{
          border: '1px solid lightgrey',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          boxShadow: '0px 0px white'
        }}
        key={Math.random()}
      />
    );
  };

  const renderTotalPaidSection = (
    amount: number,
    label: string
  ): ReactElement => {
    return (
      <Typography
        component={'div'}
        className={classes.totalPaidView}
        key={Math.random()}>
        <span>{label}</span>
        <span>
          <CurrencyFormatter
            value={amount || 0}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            ignoreRound={true}
            useSign={true}
          />
        </span>
      </Typography>
    );
  };
  const renderPaymentSectionDetails = (): ReactNode => {
    let totalAmount = 0;
    let totalRefunds = 0;
    if (invoiceLines) {
      invoiceLines.forEach((invoice: any) => {
        totalAmount += Number(invoice.amount_paid) || 0;
      });

      refunds.forEach((refund: any) => {
        totalRefunds += Number(refund.amount) || 0;
      });

      return (
        <React.Fragment>
          <Typography
            className={[
              'patient-name',
              'input-header',
              classes.headerPadding
            ].join(' ')}
            component={'div'}>
            {guarantorDetails && Object.keys(guarantorDetails).length !== 0 && (
              <span style={{}}>
                {t('PAYMENTS_LOGIN.GUARANTOR_PAYMENTS', {
                  guarantorName: HelperUtils.commaSeparatedFullName(
                    guarantorDetails?.first_name,
                    guarantorDetails?.last_name,
                    guarantorDetails?.middle_name
                  )
                })}
              </span>
            )}
          </Typography>

          {loader ? (
            <Loader />
          ) : (
            [
              renderGuarantorPayments(
                invoiceLines,
                'PAYMENTS_LOGIN.DASHBOARD.DETAILS.HEADERS.PAYMENTS_DESCRIPTION',
                'created_at',
                'amount_paid',
                'description'
              ),
              renderTotalPaidSection(
                totalAmount,
                t('PAYMENTS_LOGIN.TOTAL_PAID')
              ),
              <Divider key={'divider'} />,
              <Typography
                variant="h4"
                style={{textAlign: 'left', padding: '8px 0'}}
                key="refund-title">
                {t('PAYMENTS_LOGIN.DASHBOARD.DETAILS.HEADERS.REFUNDS')}
              </Typography>,
              renderGuarantorPayments(
                refunds,
                'PAYMENTS_LOGIN.DASHBOARD.DETAILS.HEADERS.REFUNDS_DESCRIPTION',
                'created_at',
                'amount',
                'reason'
              ),
              renderTotalPaidSection(
                totalRefunds,
                t('PAYMENTS_LOGIN.TOTAL_REFUND')
              )
            ]
          )}
        </React.Fragment>
      );
    } else {
      return <div />;
    }
  };

  return (
    <div className="payments-details-wrapper">
      <Card
        className={['payments-details-content-wrap', classes.card].join(' ')}>
        <CardContent
          className={[classes.cardPadding, 'content'].join(' ')}
          style={{backgroundColor: '#fcfcfc'}}>
          <div className="payment-details-content-view" style={{paddingTop: 0}}>
            {!isDesktop && (
              <Typography className="details-view" component={'div'}>
                <BackNavigation navigateTo={backNavigation} />
              </Typography>
            )}
            {renderPaymentSectionDetails()}
          </div>
          {(activePaymentPlan?.cancelledPlans?.length ||
            activePaymentPlan?.completedPlans?.length ||
            activePaymentPlan?.inProgressPlans?.length) && (
            <div className={classes.paymentPlanSection}>
              <div key={'divider'} className={classes.dividerView} />
              <div className={classes.paymentPlanPadding}>
                <AllPaymentPlansCard
                  guarantorId={''}
                  fromGuarantorPortal={true}
                  paymentPlan={activePaymentPlan}
                  cancelPaymentPlan={true}
                  isConvenienceFeeExist={
                    guarantorVerifyDetails?.clinic_details?.convenience_fee > 0
                      ? guarantorVerifyDetails?.clinic_details?.convenience_fee
                      : 0
                  }
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// function mapStateToProps(state: any): any {
//   return {
//     patientPaymentDetailsId: state.payments.patientPaymentDetailsId
//   };
// }
// export default connect(mapStateToProps, {
//   _patientPaymentDetailsId
// })(GuarantorPaymentDetails);

export default GuarantorPaymentDetails;
